import { useState } from "react";
import api from "../lib/api";
import { Sparkles, Loader2, Film } from "lucide-react";
import { toast } from "sonner";

export default function VideoIdeas() {
  const [form, setForm] = useState({ niche: "", subject: "", platform: "tiktok", count: 5 });
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!form.subject) return toast.error("Précise un sujet");
    setLoading(true); setIdeas([]);
    try {
      const r = await api.aiVideoIdeas({ prompt: form.subject, niche: form.niche, platform: form.platform, count: form.count });
      const m = (r.raw || "").match(/\{[\s\S]*\}/);
      if (m) {
        const p = JSON.parse(m[0]);
        setIdeas(p.ideas || []);
      } else toast.error("Format IA invalide");
    } catch { toast.error("Erreur IA"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8 animate-fadeUp" data-testid="video-ideas-page">
      <header className="border-l-2 border-[#CCFF00] pl-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// IA — IDÉES VIDÉOS</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Générateur d'idées virales</h1>
        <p className="text-zinc-400 mt-2">Hook + structure 0-1s/1-3s/3-5s/5-7s pour chaque idée. Pensé pour le watchtime maximum.</p>
      </header>

      <div className="border border-[#00D4FF]/30 bg-[#00D4FF]/[0.02] rounded-sm p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input value={form.niche} onChange={e => setForm({ ...form, niche: e.target.value })}
            placeholder="Niche" data-testid="idea-niche"
            className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white" />
          <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
            placeholder="Sujet / angle" data-testid="idea-subject"
            className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white md:col-span-2" />
          <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}
            className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="tiktok">TikTok</option><option value="instagram">Reels IG</option>
            <option value="youtube">YouTube Shorts</option>
          </select>
        </div>
        <button onClick={generate} disabled={loading} data-testid="generate-ideas-btn"
          className="bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/20 px-5 py-2.5 rounded-sm font-mono uppercase tracking-wider text-sm flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Génération..." : `Générer ${form.count} idées`}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {ideas.length === 0 ? (
          <div className="md:col-span-2 border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
            <Film className="w-10 h-10 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
            <div className="text-zinc-500 text-sm">Aucune idée générée. Lance l'IA ci-dessus.</div>
          </div>
        ) : ideas.map((idea, i) => (
          <div key={i} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 hover:border-[#00D4FF]/30 transition" data-testid={`idea-${i}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="font-display text-lg font-semibold text-white leading-tight">{idea.title}</div>
              <span className="text-[10px] font-mono uppercase tracking-wider text-[#00D4FF] shrink-0 ml-2">#{String(i+1).padStart(2, "0")}</span>
            </div>
            {idea.hook && (
              <div className="border-l-2 border-[#CCFF00] pl-3 mb-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#CCFF00]">HOOK 0-1s</div>
                <div className="text-sm text-white mt-0.5">"{idea.hook}"</div>
              </div>
            )}
            {idea.structure && (
              <div className="text-xs text-zinc-300 mb-2 whitespace-pre-line leading-relaxed">{idea.structure}</div>
            )}
            <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-3 pt-3 border-t border-[#1E1E24]">
              {idea.format && <span className="text-[#00D4FF]">{idea.format}</span>}
              {idea.why_viral && <span className="text-zinc-400 normal-case tracking-normal italic">{idea.why_viral}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
